import { useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '../stores/auth';
import { OnboardingFlow } from '../components/OnboardingFlow';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface DashboardStats {
  totalRevenue: number;
  activeLinks: number;
  totalClicks: number;
  conversionRate: number;
}

interface AnalyticsSummaryData {
  totalRevenue?: number;
  revenue?: number;
  activeLinks?: number;
  linksCount?: number;
  totalClicks?: number;
  clicks?: number;
  conversionRate?: number;
}

interface AnalyticsResponse {
  success: boolean;
  data?: AnalyticsSummaryData;
}

export function HomePage(): ReactNode {
  const { accessToken, user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, _setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    async function fetchDashboardStats(): Promise<void> {
      try {
        // Fetch affiliate analytics summary
        const res = await fetch(`${API_URL}/affiliate/analytics/summary`, {
          headers: {
            Authorization: `Bearer ${accessToken ?? ''}`,
          },
        });

        if (!res.ok) {
          // If service is unavailable or no data, use defaults
          if (res.status === 503 || res.status === 404) {
            setStats({
              totalRevenue: 0,
              activeLinks: 0,
              totalClicks: 0,
              conversionRate: 0,
            });
            return;
          }
          throw new Error('Failed to fetch dashboard stats');
        }

        const data = (await res.json()) as AnalyticsResponse;
        if (data.success && data.data !== undefined) {
          setStats({
            totalRevenue: data.data.totalRevenue ?? data.data.revenue ?? 0,
            activeLinks: data.data.activeLinks ?? data.data.linksCount ?? 0,
            totalClicks: data.data.totalClicks ?? data.data.clicks ?? 0,
            conversionRate: data.data.conversionRate ?? 0,
          });
        } else {
          // Use default values if no data
          setStats({
            totalRevenue: 0,
            activeLinks: 0,
            totalClicks: 0,
            conversionRate: 0,
          });
        }
      } catch (err) {
        console.error('Dashboard stats error:', err);
        // Don't show error, just use default values
        setStats({
          totalRevenue: 0,
          activeLinks: 0,
          totalClicks: 0,
          conversionRate: 0,
        });
      } finally {
        setLoading(false);
      }
    }

    if (accessToken !== null && accessToken !== '') {
      void fetchDashboardStats();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  // Show onboarding for new users
  useEffect(() => {
    if (!loading && stats && stats.totalRevenue === 0 && stats.activeLinks === 0) {
      // Check localStorage to see if onboarding was dismissed
      const onboardingDismissed = localStorage.getItem('onboarding-dismissed');
      if (!onboardingDismissed) {
        setShowOnboarding(true);
      }
    }
  }, [loading, stats]);

  const handleOnboardingComplete = (): void => {
    setShowOnboarding(false);
    localStorage.setItem('onboarding-dismissed', 'true');
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <>
      {showOnboarding && <OnboardingFlow onComplete={handleOnboardingComplete} />}
      <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Dashboard</h2>
        <p className="text-muted-foreground">
          Welcome back{user?.name !== null && user?.name !== undefined && user.name !== '' ? `, ${user.name}` : ''}! Here's your affiliate performance.
        </p>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg border bg-white p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-20"></div>
            </div>
          ))}
        </div>
      ) : error !== null ? (
        <div className="rounded-lg bg-red-50 p-4 text-red-700">
          {error}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats?.totalRevenue ?? 0)}
            icon={
              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Active Links"
            value={(stats?.activeLinks ?? 0).toLocaleString()}
            icon={
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            }
          />
          <StatCard
            title="Total Clicks"
            value={(stats?.totalClicks ?? 0).toLocaleString()}
            icon={
              <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
            }
          />
          <StatCard
            title="Conversion Rate"
            value={formatPercentage(stats?.conversionRate ?? 0)}
            icon={
              <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Quick Actions & Recent Activity */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <a
              href="/links"
              className="inline-flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4 hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <svg className="h-6 w-6 text-primary-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Create Link</span>
            </a>
            <a
              href="/offers"
              className="inline-flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4 hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <svg className="h-6 w-6 text-primary-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Browse Offers</span>
            </a>
            <a
              href="/content"
              className="inline-flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4 hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <svg className="h-6 w-6 text-primary-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Content Library</span>
            </a>
            <a
              href="/content?new=true"
              className="inline-flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4 hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <svg className="h-6 w-6 text-primary-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Generate Content</span>
            </a>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Top Performing Link</span>
              <span className="text-sm font-medium text-gray-900">—</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Best Converting Offer</span>
              <span className="text-sm font-medium text-gray-900">—</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Content Health Score</span>
              <span className="text-sm font-medium text-green-600">—</span>
            </div>
            <a href="/analytics" className="block text-sm text-primary-600 hover:text-primary-700 font-medium mt-4">
              View Full Analytics →
            </a>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      {stats?.totalRevenue === 0 && stats?.activeLinks === 0 && (
        <div className="rounded-lg border-2 border-primary-200 bg-primary-50 p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-semibold text-primary-900">Welcome to ProfitOS!</h3>
              <p className="mt-2 text-sm text-primary-700">
                Get started by creating your first affiliate link or browsing our marketplace for high-converting offers.
              </p>
              <div className="mt-4 flex gap-3">
                <a
                  href="/links"
                  className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Create Your First Link
                </a>
                <a
                  href="/offers"
                  className="inline-flex items-center rounded-md border border-primary-300 bg-white px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                >
                  Browse Offers
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

function StatCard({
  title,
  value,
  icon,
}: {
  title: string;
  value: string;
  icon: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        {icon}
      </div>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
